import { useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCalendar, CalendarFormat, DaysOfWeek } from '@fitogram/react-calendar';
import { bindActionCreators } from 'redux';
import { requestBookingSummaryList } from '../../store';
import { useLanguage } from '../../Hooks/useLanguage';
import { REQUEST_ACTIONS } from '../../Constants/Actions.constants';
import {
  getDateLocaleFromLanguage,
  getEndOfWeek,
  getStartOfWeek,
  useDateFns,
  useProvider,
  useUser,
} from '~/Hooks';
import { requestProviderLocationsList, requestProviderTrainerList } from '../../store/provider';
import { selectSessionData, selectLoading, requestSaveSessionData } from '../../store/app';
import {
  selectEventsCount,
  requestEventList,
  selectNextEventDateFromDate,
  selectGroupedEventsByDate,
} from '../../store/events';
import { EventType } from '~/Models/DTO/Event.model';

interface IEventListWrapperProps {
  appliedFilters?: { [key: string]: string | string[] };
  calendarFormat: CalendarFormat;
}

export const useEventListWrapper = ({ calendarFormat, appliedFilters }: IEventListWrapperProps) => {
  const { t, currentLanguage } = useLanguage();
  const { isAfterDate, addTime } = useDateFns();
  const dispatch = useDispatch();
  const { currentUser, role } = useUser();

  const { currentProvider } = useProvider();
  const lastSelectedDay = useSelector(selectSessionData('SELECTED_DAY'));
  const getEvents = bindActionCreators(requestEventList, dispatch);
  const getTrainers = bindActionCreators(requestProviderTrainerList, dispatch);
  const getLocations = bindActionCreators(requestProviderLocationsList, dispatch);
  const getBookingSummary = bindActionCreators(requestBookingSummaryList, dispatch);
  const setSessionData = bindActionCreators(requestSaveSessionData, dispatch);
  const dailyEventCount = useSelector(selectEventsCount(currentUser, role));
  const eventsLoading = useSelector(selectLoading(REQUEST_ACTIONS.GET_EVENT_LIST));
  const calendarController = useCalendar({
    startingDayOfWeek: DaysOfWeek.monday,
    availableCalendarFormats: [CalendarFormat.month, CalendarFormat.week],
    initialFormat: calendarFormat,
    initialDay: lastSelectedDay ? new Date(lastSelectedDay.data.date) : new Date(),
  });
  const { selectedDay } = calendarController;
  const onTodayClick = () => {
    calendarController.setSelectedDay(new Date(), true, false);
  };

  const events = useSelector(
    selectGroupedEventsByDate(selectedDay, currentUser, role),
  ).map((eventList) =>
    eventList.filter(
      (event) =>
        event.type !== EventType.FREE_TRAINING ||
        (event.type === EventType.FREE_TRAINING && event.public),
    ),
  );
  const eventIds = JSON.stringify(events.flatMap((eventDay) => eventDay.map((event) => event.id)));
  const nextEventDate = useSelector(
    selectNextEventDateFromDate(selectedDay.toISOString(), currentUser, role),
  );

  const noEventsThisWeek = isAfterDate(
    nextEventDate,
    getEndOfWeek(selectedDay.toISOString()).toISOString(),
  );

  const goToNextDay = (direction: number) => {
    calendarController.setSelectedDay(
      addTime(selectedDay.toString(), { days: direction }),
      true,
      false,
    );
  };

  const goToNextWeek = () => {
    calendarController.setSelectedDay(
      addTime(getEndOfWeek(selectedDay.toISOString()).toISOString(), { days: 1 }),
      true,
      false,
    );
  };

  const navigateDate = (forward: boolean) => {
    calendarController.setSelectedDay(
      addTime(selectedDay.toISOString(), { weeks: forward ? 1 : -1 }),
    );
  };

  const startOfWeek = getStartOfWeek(selectedDay.toISOString()).toISOString();
  const endOfWeek = getEndOfWeek(selectedDay.toISOString()).toISOString();

  useLayoutEffect(() => {
    if (currentProvider && startOfWeek && endOfWeek) {
      getEvents({
        providerId: currentProvider.uuid,
        from: startOfWeek,
        to: endOfWeek,
        size: 2000,
        ...appliedFilters,
      });
    }
  }, [startOfWeek, currentProvider, appliedFilters]);

  useEffect(() => {
    calendarController.updateLocale(getDateLocaleFromLanguage(currentLanguage));
  }, [currentLanguage]);

  useEffect(() => {
    if (!lastSelectedDay || selectedDay.toISOString() !== lastSelectedDay.data) {
      setSessionData('SELECTED_DAY', {
        key: 'SELECTED_DAY',
        date: selectedDay.toISOString(),
      });
    }
  }, [selectedDay]);

  useEffect(() => {
    if (currentProvider) {
      getTrainers(currentProvider.uuid);
      getLocations(currentProvider.uuid);
    }
  }, [currentProvider]);

  useEffect(() => {
    getBookingSummary({
      eventIds: JSON.parse(eventIds),
    });
  }, [selectedDay, eventIds]);

  const onNextEventClick = () => {
    calendarController.setSelectedDay(new Date(nextEventDate), true, false);
  };

  return {
    t,
    calendarController,
    onTodayClick,
    onNextEventClick,
    events,
    eventsLoading,
    dailyEventCount,
    noEventsThisWeek,
    goToNextDay,
    goToNextWeek,
    navigateDate,
  };
};
